import { useMemo } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { FilterableTable } from "../../lib/funnel/FilterableTable";
import type { ColumnSpec } from "../../lib/funnel/FilterableTable";
import { KpiCard, VBarChart, HBarChart } from "../../lib/funnel/charts";
import { downloadCsv } from "../../lib/funnel/csv";
import { RED } from "../../lib/funnel/theme";
import { PAYMENT_FAILURES_SQL, mapFailedChargeRows } from "./sql";
import type { FailedChargeRow } from "./sql";

const TRACKER_COLUMNS = [
  "Created Date (UTC)", "Amount", "Currency", "Captured", "Converted Amount",
  "Decline Reason", "Description", "Seller Message", "Customer Email",
  "Company", "User Email", "Most Recent Decline", "First Decline",
  "Month(s) of Payment Missing", "Reason for Decline", "Contact Status", "Notes",
];

function fmtDate(val: string): string {
  if (!val) return "";
  const d = new Date(val);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtAmount(amount: number, currency: string): string {
  return `${currency} ${amount.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function monthLabel(iso: string): string {
  if (!iso) return "";
  const [y, m] = iso.slice(0, 7).split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

function buildTrackerRows(charges: FailedChargeRow[]): Record<string, unknown>[] {
  const byCustomer = new Map<string, FailedChargeRow[]>();
  for (const c of charges) {
    const key = c.customer_email ?? c.company ?? c.charge_id;
    const existing = byCustomer.get(key);
    if (existing) existing.push(c);
    else byCustomer.set(key, [c]);
  }

  const out: Record<string, unknown>[] = [];
  byCustomer.forEach((group) => {
    const sorted = [...group].sort((a, b) => a.created_at_utc.localeCompare(b.created_at_utc));
    const first = sorted[0]!;
    const last = sorted[sorted.length - 1]!;
    const months = [...new Set(sorted.map((c) => monthKey(c.created_at_utc)))]
      .sort()
      .join(", ");
    out.push({
      "Created Date (UTC)": fmtDate(last.created_at_utc),
      "Amount": last.amount,
      "Currency": last.currency,
      "Captured": last.captured,
      "Converted Amount": last.converted_amount,
      "Decline Reason": last.decline_reason ?? "",
      "Description": last.description ?? "",
      "Seller Message": last.seller_message ?? "",
      "Customer Email": last.customer_email ?? "",
      "Company": last.company ?? "",
      "User Email": last.user_email ?? "",
      "Most Recent Decline": fmtDate(last.created_at_utc),
      "First Decline": fmtDate(first.created_at_utc),
      "Month(s) of Payment Missing": months,
      "Reason for Decline": "",
      "Contact Status": "",
      "Notes": "",
    });
  });

  return out.sort((a, b) =>
    String(b["Most Recent Decline"]).localeCompare(String(a["Most Recent Decline"]))
  );
}

const COLUMNS: ColumnSpec<FailedChargeRow>[] = [
  {
    key: "created_at_utc",
    label: "Created (UTC)",
    render: (_row, val) => fmtDate(String(val ?? "")),
    defaultWidth: 110,
  },
  {
    key: "amount",
    label: "Amount",
    accessor: (row) => row.amount,
    render: (row) => fmtAmount(row.amount, row.currency),
    align: "right",
    defaultWidth: 100,
  },
  {
    key: "currency",
    label: "Currency",
    filterable: true,
    defaultWidth: 80,
  },
  {
    key: "decline_reason",
    label: "Decline Reason",
    filterable: true,
  },
  {
    key: "seller_message",
    label: "Seller Message",
    render: (_row, val) =>
      val ? <span style={{ fontSize: 11 }}>{String(val)}</span> : null,
  },
  {
    key: "company",
    label: "Company",
    filterable: true,
    render: (_row, val) => val ? String(val) : <span style={{ opacity: 0.4 }}>unknown</span>,
  },
  {
    key: "user_email",
    label: "User Email",
    render: (_row, val) => val ? String(val) : <span style={{ opacity: 0.4 }}>-</span>,
  },
  {
    key: "customer_email",
    label: "Customer Email",
    render: (_row, val) => val ? String(val) : <span style={{ opacity: 0.4 }}>-</span>,
  },
];

export function PaymentFailures() {
  const result = useAdminQuery(PAYMENT_FAILURES_SQL, []);

  const rows = useMemo<FailedChargeRow[]>(() => {
    if (!result.data) return [];
    return mapFailedChargeRows(result.data.columns, result.data.rows as unknown[][]);
  }, [result.data]);

  const kpis = useMemo(() => {
    const customers = new Set(rows.map((r) => r.customer_email ?? r.company ?? r.charge_id));
    const reasons = new Map<string, number>();
    let unmatched = 0;
    for (const r of rows) {
      const reason = r.decline_reason ?? "unknown";
      reasons.set(reason, (reasons.get(reason) ?? 0) + 1);
      if (!r.company) unmatched++;
    }
    const topReason = [...reasons.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "-";
    return { total: rows.length, customers: customers.size, topReason, unmatched };
  }, [rows]);

  const byMonthData = useMemo(() => {
    const map = new Map<string, { label: string; value: number }>();
    for (const r of rows) {
      const key = monthKey(r.created_at_utc);
      if (!key) continue;
      const existing = map.get(key);
      if (existing) existing.value++;
      else map.set(key, { label: monthLabel(r.created_at_utc), value: 1 });
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([, v]) => v);
  }, [rows]);

  const byReasonData = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const reason = r.decline_reason ?? "unknown";
      map.set(reason, (map.get(reason) ?? 0) + 1);
    }
    return [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([label, value]) => ({ label, value }));
  }, [rows]);

  const handleExport = () => {
    downloadCsv(buildTrackerRows(rows), TRACKER_COLUMNS, "payment_failures_tracker");
  };

  return (
    <>
      <Topbar crumbs={["Dashboards", "Payment Failures"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Payment Failures</h1>
            <p className="lede">Failed Stripe charges. Export the tracker to work the list.</p>
          </div>
          <div className="actions">
            <button className="btn" onClick={handleExport} disabled={rows.length === 0}>
              Export Tracker CSV
            </button>
          </div>
        </div>

        {result.isLoading && <p>Loading...</p>}
        {result.isError && <p>Error loading payment failures.</p>}

        {!result.isLoading && !result.isError && (
          <>
            <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
              <KpiCard label="Failed Charges" value={String(kpis.total)} tone="danger" />
              <KpiCard label="Unique Customers" value={String(kpis.customers)} tone="danger" />
              <KpiCard label="Top Decline Reason" value={kpis.topReason} />
              <KpiCard
                label="Unmatched Customers"
                value={String(kpis.unmatched)}
                sub="No company record found"
                tone="muted"
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <VBarChart title="Failures by Month" data={byMonthData} color={RED} />
              <HBarChart title="By Decline Reason" data={byReasonData} color={RED} />
            </div>

            <div className="card" style={{ padding: "1rem" }}>
              <FilterableTable<FailedChargeRow>
                rows={rows}
                columns={COLUMNS}
                rowKey={(row) => row.charge_id}
                searchAccessor={(row) =>
                  `${row.company ?? ""} ${row.customer_email ?? ""} ${row.user_email ?? ""} ${row.decline_reason ?? ""}`
                }
                searchPlaceholder="Search by company, email, or decline reason..."
                initialSortKey="created_at_utc"
                initialSortDir="desc"
                pageSize={100}
                toolbar={
                  <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>
                    {rows.length} failed charge{rows.length !== 1 ? "s" : ""}
                  </span>
                }
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
